<template>
  <v-container fluid>
    <v-card :loading="loading">
      <v-card-title>{{ isEdit ? "Edit Blog" : "Create Blog" }}</v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="valid" @submit.prevent="save">
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field
                v-model="form.title"
                label="Title"
                counter="255"
                outlined
                dense
                :rules="[rules.required, rules.max255]"
                :error-messages="errors.title"
              />

              <v-textarea
                v-model="form.excerpt"
                label="Excerpt (optional, auto-generated if empty)"
                counter="500"
                rows="2"
                auto-grow
                outlined
                dense
                :rules="[rules.max500]"
                :error-messages="errors.excerpt"
              />

              <div class="subtitle-2 mb-1">Content</div>
              <rich-editor v-model="form.content" />
              <div
                v-if="contentError || errors.content"
                class="error--text caption mt-1"
              >
                {{ contentError || errors.content }}
              </div>
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="form.category_id"
                :items="categories"
                item-text="name"
                item-value="id"
                label="Category"
                outlined
                dense
                :rules="[rules.required]"
                :error-messages="errors.category_id"
              />

              <!-- Mini form: create a category without leaving the blog form -->
              <div class="mb-4 mt-n2">
                <v-btn
                  v-if="!showCategoryForm"
                  x-small
                  text
                  color="primary"
                  @click="showCategoryForm = true"
                >
                  <v-icon left x-small>mdi-plus</v-icon>New category
                </v-btn>

                <div v-else>
                  <div class="d-flex align-start">
                    <v-text-field
                      v-model="newCategory"
                      label="New category name"
                      counter="100"
                      maxlength="100"
                      outlined
                      dense
                      autofocus
                      :error-messages="categoryNameError || errors.name"
                      @keydown.enter.prevent="addCategory"
                    />
                    <v-btn
                      class="ml-2 mt-1"
                      small
                      color="primary"
                      :loading="creatingCategory"
                      @click="addCategory"
                    >
                      Add
                    </v-btn>
                    <v-btn
                      class="mt-1"
                      icon
                      small
                      :disabled="creatingCategory"
                      @click="cancelCategory"
                    >
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <color-picker-field
                    v-model="newCategoryColor"
                    :error-messages="errors.color"
                  />
                </div>
              </div>

              <v-select
                v-model="form.status"
                :items="statusOptions"
                label="Status"
                outlined
                dense
                :error-messages="errors.status"
                :hint="
                  isApproved
                    ? ''
                    : 'Published is available after the blog is approved. Approve it from the blogs list.'
                "
                :persistent-hint="!isApproved"
              />

              <v-switch
                v-model="form.is_main"
                label="Main blog"
                hint="Shown as the big featured card on the public blog page. Only one blog can be main, turning this on replaces the current one."
                persistent-hint
                inset
                class="mt-0 mb-4"
                :error-messages="errors.is_main"
              />

              <v-text-field
                v-model="form.slug"
                label="Slug (optional)"
                hint="Leave empty to generate from the title"
                persistent-hint
                outlined
                dense
                :error-messages="errors.slug"
              />

              <div class="subtitle-2 mt-4 mb-1">Cover image</div>
              <blog-cover-focal-picker
                v-if="form.cover_image"
                v-model="form.cover_position"
                :src="form.cover_image"
                class="mb-3"
              />
              <v-file-input
                accept="image/png,image/jpeg,image/gif,image/webp"
                label="Upload cover image"
                prepend-icon="mdi-camera"
                outlined
                dense
                show-size
                :loading="uploading"
                :value="null"
                @change="onCoverSelected"
              />
              <v-btn
                v-if="form.cover_image"
                small
                text
                color="error"
                @click="removeCover"
              >
                Remove cover
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn text :disabled="saving" @click="cancel">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" @click="save">
          {{ isEdit ? "Save changes" : "Create blog" }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import RichEditor from "./RichEditor.vue";
import ColorPickerField from "./ColorPickerField.vue";
import BlogCoverFocalPicker from "./BlogCoverFocalPicker.vue";

const fileNameFromUrl = (url) => (url || "").split("?")[0].split("/").pop();

export default {
  name: "BlogForm",
  components: { RichEditor, ColorPickerField, BlogCoverFocalPicker },
  props: {
    blogId: { type: [Number, String], default: null },
  },
  data() {
    return {
      valid: true,
      showCategoryForm: false,
      newCategory: "",
      newCategoryColor: "#2F7D0F",
      categoryNameError: "",
      creatingCategory: false,
      statuses: [
        { text: "Draft", value: "draft" },
        { text: "Published", value: "published" },
        { text: "Archived", value: "archived" },
      ],
      form: {
        title: "",
        slug: "",
        category_id: null,
        excerpt: "",
        cover_image: "",
        cover_position: "50% 50%",
        content: "",
        status: "draft",
        is_main: false,
      },
      originalCover: "",
      sessionUploads: [], // files uploaded in this form session (not saved yet)
      pendingDelete: [], // old files to delete once the blog is saved
      contentError: "",
      snackbar: { show: false, text: "", color: "error" },
      rules: {
        required: (v) => !!v || "Required",
        max255: (v) => !v || v.length <= 255 || "Max 255 characters",
        max500: (v) => !v || v.length <= 500 || "Max 500 characters",
      },
    };
  },
  computed: {
    ...mapState("blogs", [
      "categories",
      "blog",
      "loading",
      "saving",
      "uploading",
      "error",
      "errors",
    ]),
    isEdit() {
      return !!this.blogId;
    },
    isApproved() {
      return this.isEdit && !!this.blog && Number(this.blog.is_approved) === 1;
    },
    // "Published" is locked until the blog has been approved.
    statusOptions() {
      return this.statuses.map((s) => ({
        ...s,
        disabled: s.value === "published" && !this.isApproved,
      }));
    },
  },
  async created() {
    await this.getCategories();

    if (this.isEdit) {
      await this.getBlog(this.blogId);

      if (this.blog) {
        this.form = {
          title: this.blog.title,
          slug: this.blog.slug,
          category_id: this.blog.category_id,
          excerpt: this.blog.excerpt || "",
          cover_image: this.blog.cover_image || "",
          cover_position: this.blog.cover_position || "50% 50%",
          content: this.blog.content || "",
          status: this.blog.status,
          is_main: Number(this.blog.is_main) === 1,
        };
        this.originalCover = this.form.cover_image;
      }
    }
  },
  methods: {
    ...mapActions("blogs", [
      "getCategories",
      "createCategory",
      "getBlog",
      "createBlog",
      "updateBlog",
      "uploadFile",
      "deleteFile",
    ]),

    notify(text, color) {
      this.snackbar = { show: true, text, color: color || "error" };
    },

    async addCategory() {
      const name = this.newCategory.trim();

      if (!name) {
        this.categoryNameError = "Required";
        return;
      }
      this.categoryNameError = "";

      const data = new FormData();
      data.append("name", name);
      data.append("color", this.newCategoryColor);

      this.creatingCategory = true;
      const created = await this.createCategory(data);
      this.creatingCategory = false;

      // error === null means the request succeeded
      if (!created || this.error !== null) return;

      await this.getCategories();
      this.form.category_id = String(created.id); // select the new category
      this.cancelCategory();
      this.notify("Category added.", "success");
    },

    cancelCategory() {
      this.showCategoryForm = false;
      this.newCategory = "";
      this.newCategoryColor = "#2F7D0F";
      this.categoryNameError = "";
    },

    // Called when the current cover is replaced or removed.
    discardCurrentCover() {
      const url = this.form.cover_image;
      if (!url) return;
      const name = fileNameFromUrl(url);

      if (this.sessionUploads.includes(name)) {
        // Uploaded in this session and never saved: safe to delete right away.
        this.deleteFile(name);
        this.sessionUploads = this.sessionUploads.filter((n) => n !== name);
      } else if (this.isEdit && url === this.originalCover) {
        // Saved cover: delete only after the blog is saved successfully.
        this.pendingDelete.push(name);
      }
    },

    async onCoverSelected(file) {
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        this.notify("Image must be 5 MB or smaller.");
        return;
      }

      const uploaded = await this.uploadFile({ file });

      if (!uploaded) {
        if (this.errors.file) this.notify(this.errors.file);
        return;
      }

      this.discardCurrentCover();
      this.form.cover_image = uploaded.url;
      this.form.cover_position = "50% 50%"; // a new image starts centered
      this.sessionUploads.push(uploaded.file_name);
    },

    removeCover() {
      this.discardCurrentCover();
      this.form.cover_image = "";
      this.form.cover_position = "50% 50%";
    },

    hasContent() {
      const html = this.form.content || "";
      const text = html.replace(/<[^>]*>/g, "").trim();
      return !!text || /<(img|table)/i.test(html);
    },

    async save() {
      this.contentError = "";
      const ok = this.$refs.form.validate();

      if (!this.hasContent()) {
        this.contentError = "Content is required.";
        return;
      }
      if (!ok) return;

      const data = new FormData();
      Object.keys(this.form).forEach((key) => {
        const value = this.form[key];
        if (key === "is_main") {
          data.append(key, value ? "1" : "0");
          return;
        }
        data.append(key, value === null || value === undefined ? "" : value);
      });

      if (this.isEdit) {
        await this.updateBlog({ id: this.blogId, data });
      } else {
        await this.createBlog(data);
      }

      // error === null means the request succeeded
      if (this.error !== null) return;

      await Promise.all(this.pendingDelete.map((n) => this.deleteFile(n)));
      this.pendingDelete = [];
      this.sessionUploads = [];

      this.$router.push({ name: "Blogs" });
    },

    cancel() {
      // Clean up images uploaded in this session that were never saved.
      this.sessionUploads.forEach((n) => this.deleteFile(n));
      this.sessionUploads = [];
      this.$router.push({ name: "Blogs" });
    },
  },
};
</script>
